import type { ReactNode } from 'react';

import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { savePrevLocation } from 'src/utils/location-storage';
import { saveAuthToStorage } from 'src/lib/auth-storage';
import { setTokenExpiration } from 'src/auth/token-manager';
import { useAuthStore } from 'src/stores/auth-store';

import { useAuth } from 'src/features/auth/hooks/use-auth';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Decode a JWT payload without verification (the backend already signed it).
 * Returns null if the token is malformed.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-login from token query param (e.g. from MCP plugin opening browser)
  const urlToken = searchParams.get('token');
  if (urlToken && !isAuthenticated) {
    const payload = decodeJwtPayload(urlToken);
    if (payload) {
      const userId = payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] as string;
      const email = payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] as string;
      const roleClaim = payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      const roles = Array.isArray(roleClaim) ? roleClaim as string[] : roleClaim ? [roleClaim as string] : [];

      if (userId && email) {
        const authResponse = {
          token: urlToken,
          user: {
            id: userId,
            email,
            firstName: null as string | null,
            lastName: null as string | null,
            createdOnUtc: new Date().toISOString(),
            isPro: false,
            isProActive: false,
            freeTrialCount: 0,
          },
          roles,
          isSuccess: true,
          message: 'API key exchange',
        };

        saveAuthToStorage(authResponse);
        setTokenExpiration();
        useAuthStore.getState().setAuth(urlToken, authResponse.user as any, roles);
      }
    }

    // Strip the token from the URL to prevent it from being bookmarked/shared
    searchParams.delete('token');
    setSearchParams(searchParams, { replace: true });

    // Re-check after setting auth — if we just logged in, render children
    if (useAuthStore.getState().isAuthenticated()) {
      return <>{children}</>;
    }
  }

  // If already authenticated and token is in URL, just strip it
  if (urlToken && isAuthenticated) {
    searchParams.delete('token');
    setSearchParams(searchParams, { replace: true });
  }

  if (!isAuthenticated) {
    // If a return_to query param is present and points back to /oauth/authorize,
    // honor it so the post-login redirect resumes the OAuth flow.
    const returnTo = searchParams.get('return_to');
    if (returnTo) {
      try {
        const decoded = decodeURIComponent(returnTo);
        if (decoded.startsWith('/oauth/authorize')) {
          savePrevLocation(decoded);
          return <Navigate to="/sign-in" />;
        }
      } catch {
        // fall through to default behavior
      }
    }
    // Save the current location (including hash) before redirecting
    savePrevLocation(location.pathname + location.search + location.hash);
    return <Navigate to="/sign-in" />;
  }

  return <>{children}</>;
}
